import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  ButtonGroup,
  Pagination,
  Paper,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import PeopleIcon from '@mui/icons-material/People'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import TimelineIcon from '@mui/icons-material/Timeline'
import SpeedIcon from '@mui/icons-material/Speed'
import WarningIcon from '@mui/icons-material/Warning'
import PhoneIcon from '@mui/icons-material/Phone'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, PieChart, Pie, Cell, BarChart, Bar, AreaChart, ComposedChart } from 'recharts'
import { systemService } from '@/services/systemService'
import { dashboardService, DashboardSummary } from '@/services/dashboardService'
import { KPIScorecardWidget, ActivityFeedWidget, AlertCenterWidget } from '@/components/widgets'
import { kpiService } from '@/services/kpiService'
import { KPIMetric, WidgetConfig } from '@/components/widgets/types'
import DashboardBuilder from '@/components/dashboard/DashboardBuilder'
import { dashboardConfigService } from '@/services/dashboardConfigService'
import EditIcon from '@mui/icons-material/Edit'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { API_CONFIG, STORAGE_KEYS } from '@/config/env'
import { useNavigate } from 'react-router-dom'
import { userAnalyticsService, type ResolvedMsisdn } from '@/services/userAnalyticsService'

const currencyFormatter = new Intl.NumberFormat('en-KE', {
  style: 'currency',
  currency: 'KES',
  maximumFractionDigits: 0,
})
const numberFormatter = new Intl.NumberFormat('en-US')

const formatCurrency = (value: number) => currencyFormatter.format(value)
const formatNumber = (value: number) => numberFormatter.format(value)
const formatDelta = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
const cardSx = { height: '100%' }
const sectionHeaderSx = {
  px: 1.5,
  py: 1,
  borderRadius: 2,
  backgroundColor: 'action.hover',
}

const timeframeOptions = [6, 12, 24, 48]

const Dashboard = () => {
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [hours, setHours] = useState(24)
  const [systemStatus, setSystemStatus] = useState<any>(null)
  const [kpiMetrics, setKpiMetrics] = useState<KPIMetric[]>([])
  const [builderMode, setBuilderMode] = useState(false)
  const [savedWidgets, setSavedWidgets] = useState<WidgetConfig[]>([])
  const [topTransactions, setTopTransactions] = useState<{ withdrawals: any[], deposits: any[] }>({ withdrawals: [], deposits: [] })
  const [topFailureReasons, setTopFailureReasons] = useState<Array<{ source: string; reason: string; count: number }>>([])
  const [topTransactionsTab, setTopTransactionsTab] = useState(0)
  const [maskedQuery, setMaskedQuery] = useState('')
  const [resolvedMsisdnOptions, setResolvedMsisdnOptions] = useState<ResolvedMsisdn[]>([])
  const [resolvingMaskedQuery, setResolvingMaskedQuery] = useState(false)
  const navigate = useNavigate()
  
  // Pagination state
  const [page, setPage] = useState(1)
  const widgetsPerPage = 12

  const fetchSummary = useCallback(
    async (windowHours = hours) => {
      setLoading(true)
      try {
        const end = new Date()
        const start = new Date(end.getTime() - windowHours * 60 * 60 * 1000)
        const [data, status, kpis, topTx, failRes] = await Promise.all([
          dashboardService.fetchSummary(windowHours),
          systemService.fetchSystemStatus().catch(() => null),
          kpiService.getKPIMetrics(windowHours).catch(() => []),
          dashboardService.fetchTopTransactions12h().catch(() => null),
          dashboardService.fetchFailureReasons(start.toISOString(), end.toISOString(), 8).catch(() => ({ items: [] })),
        ])
        setSummary(data)
        setSystemStatus(status)
        setKpiMetrics(kpis)
        if (topTx) {
          setTopTransactions({ withdrawals: topTx.withdrawals || [], deposits: topTx.deposits || [] })
        } else {
          setTopTransactions({ withdrawals: [], deposits: [] })
        }
        setTopFailureReasons(failRes.items || [])
      } catch (error) {
        console.error('Failed to load dashboard summary', error)
      } finally {
        setLoading(false)
      }
    },
    [hours]
  )

  useEffect(() => {
    fetchSummary()
    const interval = setInterval(() => fetchSummary(), 60_000)
    return () => clearInterval(interval)
  }, [fetchSummary])

  useEffect(() => {
    const q = maskedQuery.trim()
    if (!q || q.length < 5) {
      setResolvedMsisdnOptions([])
      return
    }
    let cancelled = false
    setResolvingMaskedQuery(true)
    const t = setTimeout(async () => {
      try {
        const items = await userAnalyticsService.resolveMsisdn(q, 20)
        if (!cancelled) setResolvedMsisdnOptions(items || [])
      } catch {
        if (!cancelled) setResolvedMsisdnOptions([])
      } finally {
        if (!cancelled) setResolvingMaskedQuery(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [maskedQuery])

  useEffect(() => {
    // Load saved dashboard configuration
    const saved = localStorage.getItem('dashboard-config')
    if (saved) {
      try {
        const configs = JSON.parse(saved)
        if (Array.isArray(configs) && configs.length > 0) {
          // Get the most recent config
          const latest = configs[configs.length - 1]
          if (latest.widgets) {
            setSavedWidgets(latest.widgets)
          }
        }
      } catch {
        // Use default config if saved config is invalid
        setSavedWidgets(dashboardConfigService.getDefaultConfig())
      }
    } else {
      setSavedWidgets(dashboardConfigService.getDefaultConfig())
    }
  }, [])

  // Generate widget configs from current dashboard state
  const generateCurrentWidgetConfigs = useCallback((): WidgetConfig[] => {
    const widgets: WidgetConfig[] = []
    let yPosition = 0

    // Add KPI widgets
    if (kpiMetrics.length > 0) {
      kpiMetrics.slice(0, 6).forEach((metric, index) => {
        widgets.push({
          id: `kpi-${metric.id}`,
          type: 'kpi-scorecard',
          title: metric.label,
          position: { x: (index % 6) * 2, y: yPosition },
          size: { width: 2, height: 2 },
          refreshInterval: 60,
          filters: [],
          settings: { metricId: metric.id },
          visible: true,
        })
      })
      yPosition += 2
    } else {
      // Add fallback KPI cards
      const fallbackKPIs = [
        { id: 'success-rate', title: 'Success Rate', x: 0 },
        { id: 'deposits', title: 'Total Deposits', x: 2 },
        { id: 'withdrawals', title: 'Total Withdrawals', x: 4 },
        { id: 'net-flow', title: 'Net Flow', x: 6 },
        { id: 'active-users', title: 'Active Users', x: 8 },
      ]
      fallbackKPIs.forEach((kpi) => {
        widgets.push({
          id: `kpi-${kpi.id}`,
          type: 'kpi-scorecard',
          title: kpi.title,
          position: { x: kpi.x, y: yPosition },
          size: { width: 2, height: 2 },
          refreshInterval: 60,
          filters: [],
          settings: { metricId: kpi.id },
          visible: true,
        })
      })
      yPosition += 2
    }

    // Add Activity Feed - increased height to match Top Transactions
    widgets.push({
      id: 'activity-feed',
      type: 'activity-feed',
      title: 'Activity Feed',
      position: { x: 0, y: yPosition },
      size: { width: 6, height: 8 }, // Increased height to match Top Transactions
      refreshInterval: 30,
      filters: [],
      settings: { maxItems: 12, autoScroll: true }, // Set to 12 visible transactions
      visible: true,
    })

    // Add Top Transactions
    widgets.push({
      id: 'top-transactions',
      type: 'data-table',
      title: 'Top Transactions',
      position: { x: 6, y: yPosition },
      size: { width: 6, height: 8 }, // Increased height to show more rows
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })
    yPosition += 8

    // Alert Center widget removed as requested

    // Add Transaction Velocity
    widgets.push({
      id: 'transaction-velocity',
      type: 'custom-chart',
      title: 'Transaction Velocity',
      position: { x: 0, y: yPosition },
      size: { width: 4, height: 4 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    // Add Volume Trend - repositioned to fill space
    widgets.push({
      id: 'volume-trend',
      type: 'custom-chart',
      title: 'Volume Trend',
      position: { x: 4, y: yPosition },
      size: { width: 8, height: 4 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })
    yPosition += 4

    // Add Status Distribution - repositioned
    widgets.push({
      id: 'status-distribution',
      type: 'custom-chart',
      title: 'Status Distribution',
      position: { x: 0, y: yPosition },
      size: { width: 4, height: 4 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    // Add Paybill Performance - repositioned
    widgets.push({
      id: 'paybill-performance',
      type: 'data-table',
      title: 'Paybill Performance',
      position: { x: 4, y: yPosition },
      size: { width: 4, height: 4 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    // Add Top Channels - repositioned
    widgets.push({
      id: 'top-channels',
      type: 'data-table',
      title: 'Top Channels',
      position: { x: 8, y: yPosition },
      size: { width: 4, height: 4 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })
    yPosition += 4

    // Add Average Transaction Size - repositioned
    widgets.push({
      id: 'avg-transaction-size',
      type: 'custom-chart',
      title: 'Average Transaction Size',
      position: { x: 0, y: yPosition },
      size: { width: 4, height: 3 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    // Add Peak Hours - repositioned
    widgets.push({
      id: 'peak-hours',
      type: 'custom-chart',
      title: 'Peak Hours',
      position: { x: 4, y: yPosition },
      size: { width: 4, height: 3 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    // Add Channel Performance - replaced Processing Times
    widgets.push({
      id: 'channel-performance',
      type: 'data-table',
      title: 'Channel Performance',
      position: { x: 8, y: yPosition },
      size: { width: 4, height: 3 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })
    yPosition += 3

    // Add Failure Analysis - repositioned
    widgets.push({
      id: 'failure-analysis',
      type: 'data-table',
      title: 'Failure Analysis',
      position: { x: 0, y: yPosition },
      size: { width: 4, height: 3 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    // Add System Health - repositioned
    widgets.push({
      id: 'system-health',
      type: 'system-health',
      title: 'System Health',
      position: { x: 4, y: yPosition },
      size: { width: 4, height: 3 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    // Alerts & Signals widget removed as requested
    yPosition += 3

    // Add Recent Transactions
    widgets.push({
      id: 'recent-transactions',
      type: 'data-table',
      title: 'Recent High-Value Transactions',
      position: { x: 0, y: yPosition },
      size: { width: 12, height: 4 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    })

    return widgets
  }, [kpiMetrics])

  const handleSaveDashboard = (widgets: WidgetConfig[]) => {
    dashboardConfigService.saveConfig('My Dashboard', widgets)
    setSavedWidgets(widgets)
    setBuilderMode(false)
  }

  // Get widgets to show in builder mode - merge saved with current
  const builderWidgets = useMemo(() => {
    if (savedWidgets.length > 0) {
      // Use saved widgets if available
      return savedWidgets
    }
    // Otherwise generate from current dashboard state
    return generateCurrentWidgetConfigs()
  }, [savedWidgets, generateCurrentWidgetConfigs])

  const totals = summary?.totals
  const trendData = summary?.trend ?? []
  const netColor = totals && totals.net >= 0 ? 'success.main' : 'error.main'
  const completedPct = useMemo(
    () => summary?.statusDistribution?.find((s) => s.status === 'COMPLETED')?.percentage ?? 0,
    [summary]
  )
  const dataQualityWarnings = useMemo(() => {
    const warnings: string[] = []
    if (!summary) return warnings
    if (!summary.statusDistribution?.some((s) => s.status === 'COMPLETED')) {
      warnings.push('Status COMPLETED is missing in the selected window.')
    }
    if ((summary.channels?.length ?? 0) === 0) {
      warnings.push('No channel records for the selected window.')
    }
    if ((summary.totals?.transactions ?? 0) > 0 && completedPct < 70) {
      warnings.push(`Low completion ratio detected (${completedPct.toFixed(1)}%).`)
    }
    return warnings
  }, [summary, completedPct])

  const channelTotals = useMemo(() => summary?.channels ?? [], [summary])
  
  // Calculate total widgets for pagination
  const totalWidgets = useMemo(() => {
    let count = 0
    if (kpiMetrics.length > 0) count += Math.min(kpiMetrics.length, 6)
    else count += 5 // Fallback cards
    count += 3 // Activity Feed + Top Transactions + Alert Center
    count += 1 // Transaction Velocity
    count += 2 // Volume Trend + Status Distribution
    count += 2 // Paybill Performance + Top Channels
    count += 2 // Average Transaction Size + Peak Hours
    count += 2 // Processing Times + Failure Analysis
    count += 2 // System Health + Alerts & Signals
    count += 1 // Recent Transactions
    return count
  }, [kpiMetrics.length])
  
  const totalPages = Math.ceil(totalWidgets / widgetsPerPage)

  const handleTimeframeChange = (value: number) => {
    setHours(value)
    fetchSummary(value)
  }

  const exportDashboardCsv = useCallback(async () => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.authToken)
      const qs = new URLSearchParams({ hours: String(hours), format: 'csv' }).toString()
      const url = `${API_CONFIG.baseUrl.replace(/\/$/, '')}/api/v1/reports/dashboard-summary?${qs}`
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`CSV export failed (${res.status})`)
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `dashboard_summary_${hours}h.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      console.error(e)
    }
  }, [hours])

  const handleTrendDrilldown = useCallback(
    (timestamp: string | undefined, type: 'deposits' | 'withdrawals') => {
      if (!timestamp) return
      const start = new Date(timestamp)
      if (Number.isNaN(start.getTime())) return
      const end = new Date(start.getTime() + 60 * 60 * 1000)
      const target = type === 'deposits' ? '/deposits' : '/withdrawals'
      navigate(
        `${target}?start_date=${encodeURIComponent(start.toISOString())}&end_date=${encodeURIComponent(
          end.toISOString()
        )}`
      )
    },
    [navigate]
  )

  const handleStatusDrilldown = useCallback(
    (status?: string) => {
      if (!status) return
      navigate(`/deposits?status=${encodeURIComponent(status)}`)
    },
    [navigate]
  )

  const handleOpenResolvedActivity = useCallback(
    (msisdn: string) => {
      navigate(`/users?msisdn=${encodeURIComponent(msisdn)}`)
    },
    [navigate]
  )

  return (
    <Box>
      {/* Enhanced Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Real-Time Dashboard
          </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Chip 
                icon={<TimelineIcon />} 
                label={`Last ${summary?.timeframe.hours ?? hours} hours`} 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
              />
              {summary && (
                <Chip 
                  icon={<SpeedIcon />} 
                  label={`${summary.transactionVelocity?.current || 0} tx/min`} 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              )}
              {summary?.alerts && summary.alerts.length > 0 && (
                <Chip 
                  icon={<WarningIcon />} 
                  label={`${summary.alerts.length} Alert${summary.alerts.length > 1 ? 's' : ''}`} 
                  sx={{ bgcolor: 'rgba(255,193,7,0.3)', color: 'white' }} 
                />
              )}
            </Stack>
        </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {!builderMode && (
            <Button
              startIcon={<EditIcon />}
                variant="contained"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              onClick={() => setBuilderMode(true)}
            >
                Customize
            </Button>
          )}
            <ButtonGroup size="small" variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
            {timeframeOptions.map((option) => (
                <Button 
                  key={option} 
                  onClick={() => handleTimeframeChange(option)} 
                  variant={hours === option ? 'contained' : 'outlined'}
                  sx={{ 
                    color: hours === option ? 'white' : 'white',
                    borderColor: 'rgba(255,255,255,0.3)',
                    '&.MuiButton-contained': { bgcolor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                {option}h
              </Button>
            ))}
          </ButtonGroup>
            <Button 
              startIcon={<RefreshIcon />} 
              variant="contained"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              onClick={() => fetchSummary()}
            >
            Refresh
          </Button>
            <Button
              startIcon={<FileDownloadIcon />}
              variant="contained"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              onClick={exportDashboardCsv}
            >
              CSV
            </Button>
        </Stack>
      </Stack>
      </Paper>

      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
        </Box>
      )}
      {dataQualityWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Data quality checks</Typography>
          {dataQualityWarnings.map((warning) => (
            <Typography key={warning} variant="body2">
              - {warning}
            </Typography>
          ))}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          MSISDN Translation (masked to full)
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Autocomplete
            fullWidth
            options={resolvedMsisdnOptions}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.msisdn)}
            filterOptions={(x) => x}
            isOptionEqualToValue={(a, b) =>
              (typeof a === 'string' ? a : a.msisdn) === (typeof b === 'string' ? b : b.msisdn)
            }
            inputValue={maskedQuery}
            onInputChange={(_e, value) => setMaskedQuery(value)}
            onChange={(_e, value) => {
              if (value && typeof value !== 'string') handleOpenResolvedActivity(value.msisdn)
            }}
            loading={resolvingMaskedQuery}
            renderOption={(props, option) => (
              <li {...props} key={option.msisdn}>
                <Stack width="100%" spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{option.msisdn}</Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {option.transactionCount} tx
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          if (navigator?.clipboard) {
                            navigator.clipboard.writeText(option.msisdn).catch(() => undefined)
                          }
                        }}
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    D:{option.depositsCount ?? 0} / DD:{option.directDepositsCount ?? 0} / W:{option.withdrawalsCount ?? 0} | Net:{' '}
                    {(option.netAmount ?? 0).toFixed(2)}
                  </Typography>
                  {!!option.paybills?.length && (
                    <Typography variant="caption" color="text.secondary">
                      Paybills: {option.paybills.slice(0, 3).join(', ')}
                    </Typography>
                  )}
                  {!!option.externalUserIds?.length && (
                    <Typography variant="caption" color="text.secondary">
                      User IDs: {option.externalUserIds.slice(0, 2).join(', ')}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search masked number"
                placeholder="Example: 2547****854"
                helperText="Select result to open full activity details"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {resolvingMaskedQuery ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={() => {
              const first = resolvedMsisdnOptions[0]?.msisdn
              if (first) handleOpenResolvedActivity(first)
            }}
            disabled={!resolvedMsisdnOptions.length}
          >
            Open Activity
          </Button>
        </Stack>
      </Paper>

      {builderMode ? (
        <DashboardBuilder
          initialConfig={builderWidgets}
          onSave={handleSaveDashboard}
          onCancel={() => setBuilderMode(false)}
        />
      ) : (
        <>
          <Grid container spacing={3}>
        {/* KPI Scorecard Widgets */}
        {kpiMetrics.length > 0 ? (
          kpiMetrics.slice(0, 6).map((metric) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={metric.id}>
              <KPIScorecardWidget
                config={{
                  id: `kpi-${metric.id}`,
                  type: 'kpi-scorecard',
                  title: metric.label,
                  position: { x: 0, y: 0 },
                  size: { width: 1, height: 1 },
                  refreshInterval: 60,
                  filters: [],
                  settings: {},
                  visible: true,
                }}
                metric={metric}
                showTarget={metric.id === 'success-rate' || metric.id === 'net-flow'}
                showTrend={true}
              />
            </Grid>
          ))
        ) : (
          // Enhanced fallback cards with gradient designs
          <>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Success Rate
                    </Typography>
                      <Typography variant="h4" fontWeight="bold" mt={1}>
                    {summary?.successRate ? `${summary.successRate.current.toFixed(1)}%` : '—'}
                  </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {summary?.successRate ? `${formatDelta(summary.successRate.change)} vs previous window` : ''}
                  </Typography>
                    </Box>
                    {summary?.successRate && summary.successRate.change >= 0 ? (
                      <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Total Deposits
                    </Typography>
                      <Typography variant="h4" fontWeight="bold" mt={1}>
                    {totals ? formatCurrency(totals.deposits) : '—'}
                  </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {summary ? `${summary.timeframe.hours}h window` : ''}
                  </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Total Withdrawals
                    </Typography>
                      <Typography variant="h4" fontWeight="bold" mt={1}>
                    {totals ? formatCurrency(totals.withdrawals) : '—'}
                  </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {totals ? `${formatNumber(totals.transactions)} tx` : ''}
                  </Typography>
                    </Box>
                    <CompareArrowsIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', background: totals && totals.net >= 0 
                ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' 
                : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Net Flow
                    </Typography>
                      <Typography variant="h4" fontWeight="bold" mt={1}>
                    {totals ? formatCurrency(totals.net) : '—'}
                  </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Deposits - Withdrawals
                  </Typography>
                    </Box>
                    <AccountBalanceWalletIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Active Users
                    </Typography>
                      <Typography variant="h4" fontWeight="bold" mt={1}>
                    {totals ? formatNumber(totals.activeUsers) : '—'}
                  </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Unique MSISDNs
                  </Typography>
                    </Box>
                    <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Activity Feed Widget - Increased to show 20 items */}
        <Grid item xs={12} lg={6}>
          <ActivityFeedWidget
            config={{
              id: 'activity-feed',
              type: 'activity-feed',
              title: 'Activity Feed',
              position: { x: 0, y: 0 },
              size: { width: 1, height: 1 },
              refreshInterval: 30,
              filters: [],
              settings: { maxItems: 12 },
              visible: true,
            }}
            maxItems={12}
            autoScroll={true}
            soundAlerts={false}
            alertThreshold={10000}
          />
        </Grid>

        {/* Top Transactions Widget - Last 12 Hours */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <AccountBalanceWalletIcon color="primary" />
                <Typography variant="h6">Top Transactions (Last 12 Hours)</Typography>
              </Stack>
              <Tabs value={topTransactionsTab} onChange={(_, newValue) => setTopTransactionsTab(newValue)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Top 20 Withdrawals" />
                <Tab label="Top 20 Deposits" />
              </Tabs>
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading top transactions...
                  </Typography>
                </Box>
              ) : topTransactions ? (
                <Box>
                  {/* Withdrawals Tab */}
                  <Box sx={{ display: topTransactionsTab === 0 ? 'block' : 'none' }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Phone</strong></TableCell>
                            <TableCell align="right"><strong>Amount</strong></TableCell>
                            <TableCell><strong>Paybill</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topTransactions.withdrawals && topTransactions.withdrawals.length > 0 ? (
                            topTransactions.withdrawals.map((tx) => (
                              <TableRow key={tx.id} hover>
                                <TableCell>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <PhoneIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{tx.msisdn}</Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                                    <ArrowDownwardIcon fontSize="small" color="error" />
                                    <Typography variant="body2" fontWeight="medium" color="error">
                                      {formatCurrency(tx.amount)}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {tx.paybill || 'N/A'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No withdrawals in the last 12 hours
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  
                  {/* Deposits Tab */}
                  <Box sx={{ display: topTransactionsTab === 1 ? 'block' : 'none' }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Phone</strong></TableCell>
                            <TableCell align="right"><strong>Amount</strong></TableCell>
                            <TableCell><strong>Paybill</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topTransactions.deposits && topTransactions.deposits.length > 0 ? (
                            topTransactions.deposits.map((tx) => (
                              <TableRow key={tx.id} hover>
                                <TableCell>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <PhoneIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{tx.msisdn}</Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                                    <ArrowUpwardIcon fontSize="small" color="success" />
                                    <Typography variant="body2" fontWeight="medium" color="success.main">
                                      {formatCurrency(tx.amount)}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {tx.paybill || 'N/A'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No deposits in the last 12 hours
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No transaction data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Alert Center Widget - Removed as requested */}

        <Grid item xs={12} sx={{ order: 1 }}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="h6" fontWeight="bold">
              Operations Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Live flow, status funnel and velocity
            </Typography>
          </Box>
        </Grid>

        {/* Transaction Velocity Widget */}
        <Grid item xs={12} sm={4} md={4} lg={4} sx={{ order: 6 }}>
          <Card sx={{ ...cardSx, minHeight: 330, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Transaction Velocity
                </Typography>
                  <Typography variant="h4" fontWeight="bold" mt={1}>
                    {summary?.transactionVelocity ? `${summary.transactionVelocity.current}` : '—'}
              </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    tx/min
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                {summary?.transactionVelocity?.peakTime
                      ? `Peak: ${summary.transactionVelocity.peak} (${summary.transactionVelocity.peakTime})`
                  : 'No peak data'}
              </Typography>
                </Box>
                <SpeedIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Stack>
              {summary?.transactionVelocity?.data && summary.transactionVelocity.data.length > 0 && (
                <Box sx={{ height: 60, mt: 2, opacity: 0.9 }}>
                  <ResponsiveContainer>
                    <LineChart data={summary.transactionVelocity.data}>
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        stroke="rgba(255,255,255,0.8)" 
                        strokeWidth={2} 
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8} sx={{ order: 2 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">Volume Trend</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tracking aggregated deposits vs withdrawals over time
              </Typography>
                  <Typography variant="caption" color="text.secondary">
                    COMPLETED-only amounts (including direct deposits where available)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Click a deposits/withdrawals line point to open filtered transactions.
                  </Typography>
                </Box>
                <Chip icon={<TimelineIcon />} label="Live" color="success" size="small" />
              </Stack>
              <Box sx={{ height: 350, mt: 2 }}>
                <ResponsiveContainer>
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="colorDeposits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#2e7d32" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorWithdrawals" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#c62828" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#c62828" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="label" stroke="#666" />
                    <YAxis tickFormatter={(value) => `${Math.round(value / 1000)}k`} stroke="#666" />
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="deposits"
                      stroke="#2e7d32"
                      strokeWidth={2}
                      fill="url(#colorDeposits)"
                      name="Deposits"
                      onClick={(point: any) => handleTrendDrilldown(point?.payload?.timestamp, 'deposits')}
                    />
                    <Area
                      type="monotone"
                      dataKey="withdrawals"
                      stroke="#c62828"
                      strokeWidth={2}
                      fill="url(#colorWithdrawals)"
                      name="Withdrawals"
                      onClick={(point: any) => handleTrendDrilldown(point?.payload?.timestamp, 'withdrawals')}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4} sx={{ order: 3 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Status Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Click a segment to open filtered deposits
              </Typography>
              {summary?.statusDistribution && summary.statusDistribution.length > 0 ? (
                <Box sx={{ height: 280, mt: 2 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={summary.statusDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ status, percentage }) => `${status}: ${percentage.toFixed(1)}%`}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="count"
                        onClick={(data: any) => handleStatusDrilldown(data?.status)}
                      >
                        {summary.statusDistribution.map((_entry, index) => {
                          const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d']
                          return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} style={{ cursor: 'pointer' }} />
                        })}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  No status data available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sx={{ order: 5 }}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="h6" fontWeight="bold">
              Commercial Mix
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Paybills, channels and ticket size distribution
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} md={4} lg={4} sx={{ order: 7 }}>
          <Card sx={{ ...cardSx, minHeight: 330 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Paybill Performance
              </Typography>
              {summary?.paybillPerformance && summary.paybillPerformance.length > 0 ? (
                <List dense>
                  {summary.paybillPerformance.map((paybill) => (
                    <ListItem key={paybill.paybill} disableGutters sx={{ mb: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.dark' }}>{paybill.paybill.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">{paybill.paybill}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {paybill.share.toFixed(1)}%
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">{formatCurrency(paybill.volume)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatNumber(paybill.count)} tx
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No paybill data available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4} md={4} lg={4} sx={{ order: 8 }}>
          <Card sx={{ ...cardSx, minHeight: 330 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Channels
              </Typography>
              <List dense>
                {channelTotals.map((channel) => (
                  <ListItem key={channel.channel} disableGutters sx={{ mb: 1 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}>{channel.channel.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2">{channel.channel}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {channel.share.toFixed(1)}%
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">{formatCurrency(channel.total)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            avg {formatCurrency(channel.average)}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
                {!channelTotals.length && (
                  <Typography variant="body2" color="text.secondary">
                    No channel data available for this window.
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 9 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Average Transaction Size
              </Typography>
              <Typography variant="caption" color="text.secondary">
                COMPLETED-only averages
              </Typography>
              {summary?.averageTransactionSize ? (
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Deposits
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">{formatCurrency(summary.averageTransactionSize.deposits)}</Typography>
                        {summary.averageTransactionSize.depositsChange >= 0 ? (
                          <TrendingUpIcon color="success" fontSize="small" />
                        ) : (
                          <TrendingDownIcon color="error" fontSize="small" />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Withdrawals
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">{formatCurrency(summary.averageTransactionSize.withdrawals)}</Typography>
                        {summary.averageTransactionSize.withdrawalsChange >= 0 ? (
                          <TrendingUpIcon color="success" fontSize="small" />
                        ) : (
                          <TrendingDownIcon color="error" fontSize="small" />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Overall Average
                    </Typography>
                    <Typography variant="h5">{formatCurrency(summary.averageTransactionSize.overall)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completed ratio: {completedPct.toFixed(1)}%
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading transaction size data...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 10 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Peak Hours
              </Typography>
              {summary?.peakHours && summary.peakHours.data.length > 0 ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Peak Hour
                    </Typography>
                    <Typography variant="h5">
                      {summary.peakHours.peakHour || '—'} ({formatNumber(summary.peakHours.peakCount)} tx)
                    </Typography>
                  </Box>
                  <Box sx={{ height: 200 }}>
                    <ResponsiveContainer>
                      <BarChart data={summary.peakHours.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#1976d2" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No peak hours data available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sx={{ order: 11 }}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="h6" fontWeight="bold">
              Reliability & Risk
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Failure monitoring, alerts and platform health
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 12 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Channel Performance
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Volume and transaction count by channel
              </Typography>
              {summary?.channels && summary.channels.length > 0 ? (
                <List dense>
                  {summary.channels.slice(0, 6).map((channel) => (
                    <ListItem key={channel.channel} disableGutters sx={{ mb: 1.5 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 40, height: 40 }}>
                          {channel.channel.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" fontWeight="medium">
                              {channel.channel}
                            </Typography>
                            <Chip 
                              label={`${channel.share.toFixed(1)}%`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </Stack>
                        }
                        secondary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="primary.main" fontWeight="medium">
                              {formatCurrency(channel.total)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Avg: {formatCurrency(channel.average)}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                  {summary.channels.length > 6 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      +{summary.channels.length - 6} more channels
                    </Typography>
                  )}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No channel data available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 13 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Failure Analysis
              </Typography>
              {summary?.failureAnalysis ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Failed Transactions
                    </Typography>
                    <Typography variant="h5" color="error">
                      {summary.failureAnalysis.totalFailed}
                    </Typography>
                  </Box>
                  {summary.failureAnalysis.byChannel.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        By Channel
                      </Typography>
                      <Stack spacing={1}>
                        {summary.failureAnalysis.byChannel.map((item, idx) => (
                          <Box key={idx}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2">{item.channel}</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {item.count} ({item.percentage.toFixed(1)}%)
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {summary.failureAnalysis.byPaybill.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Top Paybills
                      </Typography>
                      <Stack spacing={0.5}>
                        {summary.failureAnalysis.byPaybill.map((item, idx) => (
                          <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">{item.paybill}</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {item.count}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {topFailureReasons.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Top Failure Reasons
                      </Typography>
                      <Stack spacing={0.75}>
                        {topFailureReasons.slice(0, 5).map((item, idx) => (
                          <Stack key={`${item.source}-${idx}`} direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="caption" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              [{item.source}] {item.reason}
                            </Typography>
                            <Typography variant="caption" fontWeight="bold">
                              {item.count}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {summary.failureAnalysis.totalFailed === 0 && (
                    <Alert severity="success">No failures in the selected window.</Alert>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading failure analysis...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 14 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Health
              </Typography>
              {systemStatus ? (
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Database
                      </Typography>
                      <Chip
                        label={systemStatus.database === 'up' ? 'Online' : 'Offline'}
                        color={systemStatus.database === 'up' ? 'success' : 'error'}
                        size="small"
                        icon={systemStatus.database === 'up' ? <CheckCircleIcon /> : <ErrorIcon />}
                      />
                    </Stack>
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        System Status
                      </Typography>
                      <Chip
                        label={systemStatus.status}
                        color={systemStatus.status === 'ready' ? 'success' : systemStatus.status === 'degraded' ? 'warning' : 'error'}
                        size="small"
                      />
                    </Stack>
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Response Time
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {systemStatus.responseTimeMs}ms
                      </Typography>
                    </Stack>
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Environment
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {systemStatus.environment || '—'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading system status...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 15 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <NotificationsActiveIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                Alerts & Signals
              </Typography>
                {summary?.alerts && summary.alerts.length > 0 && (
                  <Chip 
                    label={summary.alerts.length} 
                    color="error" 
                    size="small" 
                    sx={{ ml: 'auto' }}
                  />
                )}
              </Stack>
              <Stack spacing={2}>
                {summary?.alerts && summary.alerts.length > 0 ? (
                  <>
                    {summary.alerts.map((alert, idx) => (
                      <Alert 
                        key={idx} 
                        severity={alert.type}
                        icon={alert.type === 'error' ? <ErrorIcon /> : alert.type === 'warning' ? <WarningIcon /> : alert.type === 'success' ? <CheckCircleIcon /> : <CheckCircleIcon />}
                        sx={{ 
                          boxShadow: 1,
                          '& .MuiAlert-icon': { fontSize: 28 }
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                      {alert.message}
                        </Typography>
                    </Alert>
                    ))}
                    {/* Alert Summary Chart */}
                    <Box sx={{ mt: 2, height: 150 }}>
                      <ResponsiveContainer>
                        <BarChart data={[
                          { name: 'Errors', value: summary.alerts.filter(a => a.type === 'error').length, color: '#d32f2f' },
                          { name: 'Warnings', value: summary.alerts.filter(a => a.type === 'warning').length, color: '#ed6c02' },
                          { name: 'Info', value: summary.alerts.filter(a => a.type === 'info').length, color: '#0288d1' },
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#8884d8">
                            {[
                              { name: 'Errors', value: summary.alerts.filter(a => a.type === 'error').length, color: '#d32f2f' },
                              { name: 'Warnings', value: summary.alerts.filter(a => a.type === 'warning').length, color: '#ed6c02' },
                              { name: 'Info', value: summary.alerts.filter(a => a.type === 'info').length, color: '#0288d1' },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </>
                ) : (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    No alerts for the selected window. System operating normally.
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sx={{ order: 16 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Recent High-Value Transactions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>MSISDN</strong></TableCell>
                    <TableCell><strong>Paybill</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary?.recentTransactions.map((tx) => (
                    <TableRow key={tx.id} hover>
                      <TableCell>
                        <Chip
                          label={tx.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          color={tx.type === 'deposit' ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{tx.msisdn}</TableCell>
                      <TableCell>{tx.paybill || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(tx.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!summary?.recentTransactions.length && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          No transactions recorded in this window.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => {
              setPage(value)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
        </>
      )}
    </Box>
  )
}

export default Dashboard

